import {
  Air as AirIcon,
  Animation as AnimationIcon,
  Autorenew as AutorenewIcon,
  BatchPrediction as BatchPredictionIcon,
  Settings as SettingsIcon,
  ViewArray as ViewArrayIcon,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  Popover,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MouseEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  BaseDropdown,
  BaseDropdownTrigger,
  DropdownTypography,
  StyledSwitch,
  baseDropdownClasses,
} from '#entities';
import { routes } from '#shared';

import { useAppDispatch } from '../../redux-store';
import { blockSelectors } from '../../slices';
import { toggleNewCasesBlocks } from '../../thunks';
import {
  useSelectIsHighlightOn,
  useSelectIsRotationOn,
  useSelectIsWeatherAnimationOn,
  useSelectIsWindRotationOn,
  widgetManagerActions,
} from '../../widgetManager';

interface SettingsDropdownProps {
  isIdle: boolean;
}

const StyledPopover = styled(Popover)(() => ({
  '& .MuiPaper-root': {
    animation: 'fadeInScale 300ms ease-out',
    transformOrigin: 'top left',
  },
}));

const SettingsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1, 2),
}));

const SettingsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const IconLabelBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const SettingsDropdown = ({ isIdle }: SettingsDropdownProps) => {
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { palette, breakpoints } = useTheme();

  const rotationOn = useSelectIsRotationOn();
  const windRotationOn = useSelectIsWindRotationOn();
  const weatherAnimationOn = useSelectIsWeatherAnimationOn();
  const highlightOn = useSelectIsHighlightOn();
  const hasNewCasesAside = blockSelectors.useSelectNewCasesBlocksVisible();
  const isBigDevice = useMediaQuery(breakpoints.up('xl'));
  const open = Boolean(anchorEl) && !isIdle;
  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <BaseDropdownTrigger
        className={baseDropdownClasses.button}
        onClick={handleClick}
      >
        <SettingsIcon
          sx={{ color: palette.common.white }}
          className={baseDropdownClasses.icon}
        />
      </BaseDropdownTrigger>

      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={0}
      >
        <BaseDropdown className={baseDropdownClasses.root}>
          <SettingsSection className={baseDropdownClasses.section}>
            <Link to={routes.SETTINGS} style={{ textDecoration: 'none' }}>
              <DropdownTypography className={baseDropdownClasses.iconRow}>
                <SettingsIcon
                  fontSize="small"
                  className={baseDropdownClasses.icon}
                />
                Settings
              </DropdownTypography>
            </Link>
          </SettingsSection>

          <Divider className={baseDropdownClasses.divider} />

          <SettingsSection className={baseDropdownClasses.section}>
            <SettingsRow>
              <IconLabelBox>
                <AutorenewIcon className={baseDropdownClasses.icon} />
                <DropdownTypography>Rotate cases</DropdownTypography>
              </IconLabelBox>
              <StyledSwitch
                checked={rotationOn}
                onChange={e =>
                  dispatch(widgetManagerActions.setRotationOn(e.target.checked))
                }
                size="small"
              />
            </SettingsRow>

            <SettingsRow>
              <IconLabelBox>
                <AirIcon className={baseDropdownClasses.icon} />
                <DropdownTypography>Rotate wind map</DropdownTypography>
              </IconLabelBox>
              <StyledSwitch
                checked={windRotationOn}
                onChange={e =>
                  dispatch(
                    widgetManagerActions.setWindRotationOn(e.target.checked),
                  )
                }
                size="small"
              />
            </SettingsRow>

            <SettingsRow>
              <IconLabelBox>
                <AnimationIcon className={baseDropdownClasses.icon} />
                <DropdownTypography>
                  Animate weather autostart
                </DropdownTypography>
              </IconLabelBox>
              <StyledSwitch
                checked={weatherAnimationOn}
                onChange={e =>
                  dispatch(
                    widgetManagerActions.setWeatherAnimationOn(
                      e.target.checked,
                    ),
                  )
                }
                size="small"
              />
            </SettingsRow>

            <SettingsRow>
              <IconLabelBox>
                <BatchPredictionIcon className={baseDropdownClasses.icon} />
                <DropdownTypography>Highlight updates</DropdownTypography>
              </IconLabelBox>
              <StyledSwitch
                checked={highlightOn}
                onChange={e =>
                  dispatch(
                    widgetManagerActions.setHighlightOn(e.target.checked),
                  )
                }
                size="small"
              />
            </SettingsRow>
            {isBigDevice ? (
              <SettingsRow>
                <IconLabelBox>
                  <ViewArrayIcon className={baseDropdownClasses.icon} />
                  <DropdownTypography>New Cases Aside</DropdownTypography>
                </IconLabelBox>
                <StyledSwitch
                  checked={hasNewCasesAside}
                  onChange={e =>
                    dispatch(toggleNewCasesBlocks(e.target.checked))
                  }
                  size="small"
                />
              </SettingsRow>
            ) : null}
          </SettingsSection>
        </BaseDropdown>
      </StyledPopover>
    </>
  );
};
